import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';
import { TfService } from 'src/app/tf/tf-service.service';
import { CustomToastService } from '../service/toast.service';

const SIGN_IN_POLICY_ERROR_CODES = ['MFA_ENFORCED', 'MFA_UNAVAILABLE'];

interface ErrorMessage {
  fieldName: string;
  message: string;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private TfService: TfService, private toastr: CustomToastService) { }

  /**
   * Intercepts HTTP requests to handle errors and display appropriate messages.
   * @param request - The outgoing HTTP request.
   * @param next - The next interceptor in the chain.
   * @returns An observable of the HTTP event.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        const url = err.url ?? request.url;
        const isAuthEndpoint = /\/auth\/(login|refresh|logout|status|reset|password-reset-complete)/.test(url);
        const isLoginEndpoint = /\/auth\/login/.test(url);
        const isLoginChallenge = !!err.error?.challenge;
        // These screens translate their own error codes; a raw global toast would duplicate them.
        const handlesOwnErrors = /\/auth\/(security-config|mfa)\//.test(url);
        const errorCode = typeof err.error?.code === 'string' ? err.error.code : '';
        const isSignInPolicyError = SIGN_IN_POLICY_ERROR_CODES.includes(errorCode);

        // Login / Cognito challenges are handled on the auth screen — no global toast.
        // AUTH_CONFIG_CHANGED redirects to the login page, which explains it.
        if (isLoginEndpoint || isLoginChallenge || handlesOwnErrors || errorCode === 'AUTH_CONFIG_CHANGED') {
          return throwError(() => err);
        }

        // 401 is handled by JwtInterceptor (refresh + retry). Only force logout on 403 for protected APIs.
        // Sign-in policy 403s (e.g. disabling TOTP while MFA is enforced) are not session failures.
        if (err.status === 403 && !isAuthEndpoint && !isLoginChallenge && !isSignInPolicyError) {
          this.TfService.logout().subscribe({
            next: () => {
              console.log('User logged out due to forbidden access');
            },
            error: (logoutError) => {
              console.error('Logout failed:', logoutError);
            }
          });
        }

        // Extract user-friendly error message from the error response
        const errorMessage = this.extractErrorMessage(err);

        // Display different error messages based on error status text
        if (err.statusText === "Internal Server Error") {
          // Display a generic internal server error message
          this.toastr.showError('Internal Server Error');
        } else if (errorMessage.length > 0) {
          // Display the extracted error message
          const errorMessageString = `${errorMessage[0].fieldName}: ${errorMessage[0].message}`;
          this.toastr.showError(errorMessageString);
        }

        // Re-throw the error for further handling if necessary
        return throwError(() => err);
      })
    );
  }

  private extractErrorMessage(errorResponse: HttpErrorResponse): ErrorMessage[] {
    const errorMessages: ErrorMessage[] = [];
    const errorData = errorResponse.error;

    function recurseErrors(data: any, parentKey: string = '') {
      if (Array.isArray(data)) {
        data.forEach((item, idx) => {
          recurseErrors(item, parentKey ? `${parentKey}[${idx}]` : `${idx}`);
        });
      } else if (typeof data === 'object' && data !== null) {
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            recurseErrors(data[key], parentKey ? `${parentKey}.${key}` : key);
          }
        }
      } else {
        // data is a string or primitive
        errorMessages.push({ fieldName: parentKey, message: String(data) });
      }
    }

    recurseErrors(errorData);

    // Fallback: if no messages found, show a generic error
    if (errorMessages.length === 0) {
      errorMessages.push({ fieldName: 'error', message: 'An unknown error occurred.' });
    }

    return errorMessages;
  }
}